import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Paper, TextField, Button, CircularProgress, Chip, Snackbar, IconButton, Breadcrumbs,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import EditIcon from "@material-ui/icons/Edit";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import {
  getNotificationRecipients, setToRecipient, addCcRecipient, deleteNotificationRecipient,
} from "app/api/notification-recipients.api";

const useStyles = makeStyles(() => ({
  breadcrumb: {
    marginBottom: 8,
    "& .MuiBreadcrumbs-separator": { color: "#98A2B3" },
  },
  crumbMuted: { fontSize: 13, color: "#667085" },
  crumbCurrent: { fontSize: 13, fontWeight: 600, color: "#344054" },
  pageHead: { marginBottom: 24 },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#101828",
    lineHeight: "30px",
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#667085",
    marginTop: 4,
  },
  card: {
    border: "1px solid #E4E7EC",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
    overflow: "hidden",
  },
  section: {
    padding: "20px 24px",
    borderBottom: "1px solid #F2F4F7",
    "&:last-child": { borderBottom: "none" },
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 4,
  },
  sectionIcon: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#EEF2F3",
    color: "#3A4D54",
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#101828",
  },
  sectionCount: {
    fontSize: 12.5,
    color: "#98A2B3",
  },
  sectionHint: {
    fontSize: 12.5,
    color: "#667085",
    margin: "0 0 12px 40px",
  },
  body: { marginLeft: 40 },
  toRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 8,
    backgroundColor: "#F6F9FA",
    border: "1px solid #DDE6E9",
    maxWidth: 520,
  },
  toName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#101828",
    lineHeight: "20px",
  },
  toEmail: {
    fontSize: 13,
    color: "#475467",
    lineHeight: "18px",
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  recipientChip: {
    backgroundColor: "#EEF2F3",
    color: "#344054",
    fontSize: 12.5,
  },
  noRecipients: {
    fontSize: 13,
    color: "#98A2B3",
    marginBottom: 12,
  },
  addRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  addField: {
    "& .MuiOutlinedInput-input": { fontSize: 13.5 },
  },
  emailField: { width: 260 },
  nameField: { width: 180 },
  addBtn: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    padding: "6px 18px",
    backgroundColor: "#3A4D54",
    color: "#fff",
    "&:hover": { backgroundColor: "#2d3b41" },
    "&:disabled": { backgroundColor: "#D0D5DD", color: "#98A2B3" },
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: "40px 24px",
  },
}));

export default function NotificationSettingsPage() {
  const classes = useStyles();
  const [toRecipient, setTo] = useState(null);
  const [ccRecipients, setCc] = useState([]);
  const [maxCc, setMaxCc] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [toForm, setToForm] = useState({ email: "", name: "" });
  const [ccForm, setCcForm] = useState({ email: "", name: "" });
  const [isEditingTo, setIsEditingTo] = useState(false);
  const [busy, setBusy] = useState(null);
  const [snack, setSnack] = useState(null);

  const load = useCallback(async () => {
    try {
      const recipientsRes = await getNotificationRecipients();
      const data = recipientsRes.data.data || {};
      setTo(data.to || null);
      setCc(data.cc || []);
      setMaxCc(data.max_cc || 10);
      setIsEditingTo(!data.to);
    } catch (e) {
      setSnack({ severity: "error", message: e.response?.data?.message || "Failed to load notification settings" });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveTo = async () => {
    if (!toForm.email.trim()) return;
    setBusy("to");
    try {
      const payload = { email: toForm.email.trim() };
      if (toForm.name.trim()) payload.name = toForm.name.trim();
      await setToRecipient(payload);
      setToForm({ email: "", name: "" });
      setSnack({ severity: "success", message: "Recipient updated." });
      await load();
    } catch (e) {
      setSnack({ severity: "error", message: e.response?.data?.message || "Failed to set recipient" });
    }
    setBusy(null);
  };

  const handleAddCc = async () => {
    if (!ccForm.email.trim()) return;
    setBusy("cc");
    try {
      const payload = { email: ccForm.email.trim() };
      if (ccForm.name.trim()) payload.name = ccForm.name.trim();
      await addCcRecipient(payload);
      setCcForm({ email: "", name: "" });
      setSnack({ severity: "success", message: "CC recipient added." });
      await load();
    } catch (e) {
      setSnack({ severity: "error", message: e.response?.data?.message || "Failed to add CC recipient" });
    }
    setBusy(null);
  };

  const handleRemove = async (recipient, successMessage) => {
    setBusy(recipient.uuid);
    try {
      await deleteNotificationRecipient(recipient.uuid);
      setSnack({ severity: "success", message: successMessage });
      await load();
    } catch (e) {
      setSnack({ severity: "error", message: e.response?.data?.message || "Failed to remove recipient" });
    }
    setBusy(null);
  };

  const startEditTo = () => {
    setToForm({ email: toRecipient?.email || "", name: toRecipient?.name || "" });
    setIsEditingTo(true);
  };

  return (
    <Box>
      <Breadcrumbs
        className={classes.breadcrumb}
        separator={<NavigateNextIcon style={{ fontSize: 16 }} />}
        aria-label="breadcrumb"
      >
        <Typography className={classes.crumbMuted}>Credit Transfer</Typography>
        <Typography className={classes.crumbCurrent}>Notification Settings</Typography>
      </Breadcrumbs>
      <Box className={classes.pageHead}>
        <Typography className={classes.pageTitle}>Notification Settings</Typography>
        <Typography className={classes.pageSubtitle}>
          Choose who is emailed whenever your team completes a credit transfer — for any company.
        </Typography>
      </Box>

      <Paper className={classes.card} elevation={0}>
        {isLoading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* To */}
            <Box className={classes.section}>
              <Box className={classes.sectionHead}>
                <Box className={classes.sectionIcon}>
                  <MailOutlineIcon style={{ fontSize: 16 }} />
                </Box>
                <Typography className={classes.sectionTitle}>To</Typography>
                <Typography className={classes.sectionCount}>main recipient</Typography>
              </Box>
              <Typography className={classes.sectionHint}>
                The notification email is addressed to this person. Without it, no notification is sent.
              </Typography>
              <Box className={classes.body}>
                {toRecipient && !isEditingTo ? (
                  <Box className={classes.toRow}>
                    <Box flex={1}>
                      {toRecipient.name && <Typography className={classes.toName}>{toRecipient.name}</Typography>}
                      <Typography className={classes.toEmail}>{toRecipient.email}</Typography>
                    </Box>
                    <IconButton size="small" onClick={startEditTo} aria-label="Change recipient">
                      <EditIcon style={{ fontSize: 16, color: "#475467" }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box className={classes.addRow}>
                    <TextField
                      variant="outlined" size="small" type="email"
                      placeholder="email@example.com"
                      className={`${classes.addField} ${classes.emailField}`}
                      value={toForm.email}
                      onChange={(e) => setToForm({ ...toForm, email: e.target.value })}
                    />
                    <TextField
                      variant="outlined" size="small"
                      placeholder="Name (optional)"
                      className={`${classes.addField} ${classes.nameField}`}
                      value={toForm.name}
                      onChange={(e) => setToForm({ ...toForm, name: e.target.value })}
                    />
                    <Button
                      className={classes.addBtn}
                      onClick={handleSaveTo}
                      disabled={!toForm.email.trim() || busy === "to"}
                      disableElevation
                    >
                      {busy === "to" ? <CircularProgress size={16} style={{ color: "#98A2B3" }} /> : "Save"}
                    </Button>
                    {toRecipient && (
                      <Button
                        onClick={() => { setIsEditingTo(false); setToForm({ email: "", name: "" }); }}
                        style={{ fontSize: 13, fontWeight: 600, textTransform: "none", color: "#344054" }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* CC */}
            <Box className={classes.section}>
              <Box className={classes.sectionHead}>
                <Box className={classes.sectionIcon}>
                  <PeopleOutlineIcon style={{ fontSize: 16 }} />
                </Box>
                <Typography className={classes.sectionTitle}>CC</Typography>
                <Typography className={classes.sectionCount}>{ccRecipients.length}/{maxCc}</Typography>
              </Box>
              <Typography className={classes.sectionHint}>
                These people also receive the same notification email as the main recipient above. Add up to {maxCc}.
              </Typography>
              <Box className={classes.body}>
                {ccRecipients.length === 0 ? (
                  <Typography className={classes.noRecipients}>No CC recipients yet</Typography>
                ) : (
                  <Box className={classes.chipsRow}>
                    {ccRecipients.map((recipient) => (
                      <Chip
                        key={recipient.uuid}
                        className={classes.recipientChip}
                        label={recipient.name ? `${recipient.name} — ${recipient.email}` : recipient.email}
                        onDelete={() => handleRemove(recipient, "CC recipient removed.")}
                        disabled={busy === recipient.uuid}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
                <Box className={classes.addRow}>
                  <TextField
                    variant="outlined" size="small" type="email"
                    placeholder="email@example.com"
                    className={`${classes.addField} ${classes.emailField}`}
                    value={ccForm.email}
                    onChange={(e) => setCcForm({ ...ccForm, email: e.target.value })}
                  />
                  <TextField
                    variant="outlined" size="small"
                    placeholder="Name (optional)"
                    className={`${classes.addField} ${classes.nameField}`}
                    value={ccForm.name}
                    onChange={(e) => setCcForm({ ...ccForm, name: e.target.value })}
                  />
                  <Button
                    className={classes.addBtn}
                    onClick={handleAddCc}
                    disabled={!ccForm.email.trim() || busy === "cc" || ccRecipients.length >= maxCc}
                    disableElevation
                  >
                    {busy === "cc" ? <CircularProgress size={16} style={{ color: "#98A2B3" }} /> : "Add"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity={snack?.severity || "info"} onClose={() => setSnack(null)}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
